<script setup lang="ts">
import { ref, computed, watch } from "vue";
import { useRoute, useRouter } from "vue-router";
import { Drawer, Button } from "primevue";
import { useToast } from "primevue/usetoast";
import SingleThread from "@/components/messaging/SingleThread.vue";
import MessageThreadList from "@/components/messaging/MessageThreadList.vue";
import DetailsPane from "@/components/messaging/DetailsPane.vue";
import { useThreadStore } from "@/stores/thread.store";
import { ThreadsApi, Configuration } from "@/api/javaapi";
import { useAuthStore } from "@/stores/auth.store";
import { useChatWindowStore } from '@/stores/chatWindow.store';

defineProps<{
  showNewMessage?: boolean;
  reservationNumber?: string;
}>();

const route = useRoute();
const router = useRouter();

const threadStore = useThreadStore();
const authStore = useAuthStore();
const toast = useToast();

const threadApi = new ThreadsApi(
  new Configuration({ accessToken: authStore.token ?? undefined })
);

const showDetails = ref<boolean>(false);

const chatWindowStore = useChatWindowStore();

function openInChatWindow(threadId: number) {
  chatWindowStore.openThread(threadId);
}

const threadId = computed((): number => {
  const id = route.params.id;
  return parseInt(Array.isArray(id) ? id[0] : id || "0");
});
const currentThread = computed(() => threadStore.openThreads.get(threadId.value)?.thread);

const isArchived = computed(() => currentThread.value?.archived ?? false);

const threadTitle = computed(() => {
  return currentThread.value?.guestName || currentThread.value?.subject || "Messages";
});

const archiveThread = async () => {
  if (!threadId.value) return;

  try {
    await threadApi.archiveThread(threadId.value);
    threadStore.threadList = threadStore.threadList.filter((t) => t.ID !== threadId.value);
    router.push("/inbox");
    toast.add({ severity: "success", summary: "Thread Archived", detail: "The thread has been archived.", life: 3000 });
  } catch (error) {
    console.error("Failed to archive thread:", error);
    toast.add({ severity: "error", summary: "Archive Failed", detail: "Could not archive the thread.", life: 3000 });
  }
};

const unarchiveThread = async () => {
  if (!threadId.value) return;

  try {
    await threadApi.unarchiveThread(threadId.value);
    const thread = threadStore.getOpenThread(threadId.value);
    if (thread) {
      thread.archived = false;
      threadStore.updateThreadInList(thread);
    }
    toast.add({ severity: "success", summary: "Thread Unarchived", detail: "The thread has been restored.", life: 3000 });
  } catch (error) {
    console.error("Failed to unarchive thread:", error);
    toast.add({ severity: "error", summary: "Unarchive Failed", detail: "Could not unarchive the thread.", life: 3000 });
  }
};

const markAsUnread = async () => {
  if (!threadId.value) return;

  try {
    await threadApi.markThreadAsUnread(threadId.value);
    const thread = threadStore.getOpenThread(threadId.value);
    if (thread) {
      thread.newStatus = true;
      threadStore.updateThreadInList(thread);
    }
  } catch (error) {
    console.error("Failed to mark thread as unread:", error);
    toast.add({ severity: "error", summary: "Action Failed", detail: "Could not mark the thread as unread.", life: 3000 });
  }
};
</script>

<template>
  <div class="grid grid-cols-1 md:grid-cols-[minmax(0,330px)_1fr] lg:grid-cols-[minmax(0,400px)_1fr_minmax(0,400px)] h-full bg-gray-50 dark:bg-gray-900">
    <!-- Thread List Column -->
    <div class="col-span-1 overflow-hidden bg-gray-100 border-b dark:bg-gray-900" :class="{ 'hidden md:block': threadId }">
      <MessageThreadList 
        class="border-e h-full" 
        :show-new-message="showNewMessage"
        :initial-reservation-number="reservationNumber"
      />
    </div>

    <!-- Thread Content Column -->
    <div class="col-span-1 flex flex-col overflow-hidden" :class="{ 'md:col-span-1': $route.params.id, 'hidden md:block': !$route.params.id }">
      <div class="flex justify-between items-center p-2 bg-gray-100 border-b dark:bg-gray-900">
        <Button
          icon="pi pi-arrow-left"
          class="p-button-text p-button-rounded p-button-sm md:hidden"
          @click="$router.push('/inbox')"
        />
        <span class="text-gray-900 dark:text-gray-100 font-semibold truncate flex-1 text-center">{{ threadTitle }}</span>
        <div class="flex gap-2">
          <Button
            v-if="$route.params.id && !isArchived"
            icon="pi pi-arrow-circle-down"
            class="p-button-text p-button-rounded p-button-sm"
            v-tooltip.bottom="'Archive'"
            @click="archiveThread"
          />
          <Button
            v-if="$route.params.id && isArchived"
            icon="pi pi-inbox"
            class="p-button-text p-button-rounded p-button-sm"
            v-tooltip.bottom="'Unarchive'"
            @click="unarchiveThread"
          />
          <Button
            v-if="$route.params.id"
            icon="pi pi-envelope"
            class="p-button-text p-button-rounded p-button-sm"
            v-tooltip.bottom="'Mark as Unread'"
            @click="markAsUnread"
          />
          <Button
            icon="pi pi-info"
            class="p-button-text p-button-rounded p-button-sm md:block lg:hidden"
            v-tooltip.bottom="'Details'"
            @click="showDetails = true"
          />
          <Button
            icon="pi pi-window-minimize"
            class="p-button-text p-button-rounded p-button-sm -scale-x-100"
            v-tooltip.bottom="'Open in chat window'"
            @click="openInChatWindow(threadId)"
          />
        </div>
      </div>
      <div class="flex-grow overflow-hidden">
        <SingleThread v-if="$route.params.id" :thread-id="threadId" class="h-full" />
        <p v-else class="text-center text-gray-500 dark:text-gray-400">Select a thread to view messages</p>
      </div>
    </div>

    <!-- Details Pane Column -->
    <div class="col-span-1 hidden lg:block overflow-y-scroll border-s bg-gray-100 dark:bg-gray-900">
      <DetailsPane v-if="$route.params.id" :thread-id="threadId" class="h-full" />
    </div>
  </div>

  <!-- Details Drawer -->
  <Drawer v-model:visible="showDetails" position="right" class="w-80 lg:hidden">
    <DetailsPane v-if="$route.params.id" :thread-id="threadId" />
  </Drawer>
</template>
<style>
.p-drawer-content {
  --p-drawer-content-padding: 0.1rem;
}
.p-drawer-header {
  --p-drawer-header-padding: 0.1rem;
}
</style>